import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const sections = [
  { id: 'scene', icon: '📍', name: 'Sur scène & sur la route', desc: 'Un concert, une tournée' },
  { id: 'rencontre', icon: '🤝', name: 'Rencontres & hasards', desc: "Je l'ai croisé dans la vie" },
  { id: 'etsi', icon: '✨', name: 'Et si…', desc: "Si j'avais pu le rencontrer" },
  { id: 'art', icon: '🎨', name: 'Art des fans', desc: 'Peinture, dessin, gravure' },
  { id: 'documentaire', icon: '🎬', name: 'Le documentaire', desc: 'Ce que le monde doit savoir' },
  { id: 'photos', icon: '📸', name: 'Photos & vidéos rares de MJ', desc: 'Partager un média rare ou inédit' },
  { id: 'actualite', icon: '\uD83D\uDCF0', name: 'Une actualité', desc: 'Partager une actu à relayer' },
  { id: 'info', icon: '\u2139\uFE0F', name: 'Une info sur Michael ou autre', desc: 'Une information, une suggestion…' },
];

const pays = ['Afghanistan','Afrique du Sud','Albanie','Algérie','Allemagne','Angola','Arabie Saoudite','Argentine','Australie','Autriche','Azerbaïdjan','Bahreïn','Bangladesh','Belgique','Bénin','Biélorussie','Bolivie','Bosnie-Herzégovine','Brésil','Bulgarie','Burkina Faso','Burundi','Cambodge','Cameroun','Canada','Chili','Chine','Colombie','Congo','Corée du Sud','Costa Rica','Côte d\'Ivoire','Croatie','Cuba','Danemark','Égypte','Émirats arabes unis','Équateur','Espagne','Estonie','Éthiopie','États-Unis','Finlande','France','Gabon','Ghana','Grèce','Guatemala','Guinée','Haïti','Honduras','Hongrie','Inde','Indonésie','Irak','Iran','Irlande','Islande','Israël','Italie','Jamaïque','Japon','Jordanie','Kazakhstan','Kenya','Koweït','Lettonie','Liban','Libye','Lituanie','Luxembourg','Madagascar','Malaisie','Mali','Maroc','Mauritanie','Mexique','Moldavie','Mongolie','Mozambique','Namibie','Nicaragua','Niger','Nigéria','Norvège','Nouvelle-Zélande','Ouganda','Ouzbékistan','Pakistan','Panama','Paraguay','Pays-Bas','Pérou','Philippines','Pologne','Portugal','Qatar','République tchèque','Roumanie','Royaume-Uni','Russie','Rwanda','Sénégal','Serbie','Singapour','Slovaquie','Slovénie','Somalie','Soudan','Sri Lanka','Suède','Suisse','Syrie','Taïwan','Tanzanie','Thaïlande','Togo','Tunisie','Turquie','Ukraine','Uruguay','Venezuela','Vietnam','Yémen','Zimbabwe'];

export default function Temoigner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    section: '',
    authorName: '',
    authorCountry: '',
    city: '',
    year: '',
    eventName: '',
    content: '',
    rightsAccepted: false,
    videoUrl: '',
  });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (user) setForm(p => ({ ...p, authorName: p.authorName || user.username || user.pseudo || user.prenom || '' }));
  }, [user]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFiles = (newFiles) => {
    const MAX = 5 * 1024 * 1024 * 1024; // 5 Go
    const valid = Array.from(newFiles).filter(f => {
      if (f.size > MAX) { setError(`"${f.name}" dépasse la limite de 5 Go.`); return false; }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
    setError('');
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!form.section) return setError('Veuillez choisir une section.');
    const isSubmission = form.section === 'actualite' || form.section === 'info';
    if (!isSubmission) {
      if (!form.authorName) return setError('Veuillez entrer votre prénom ou pseudonyme.');
      if (!form.authorCountry) return setError('Veuillez sélectionner votre pays.');
      if (!form.content || form.content.length < 20) return setError('Votre témoignage est trop court (minimum 20 caractères).');
      if (!form.rightsAccepted) return setError('Veuillez accepter les conditions de cession de droits.');
    }
    setError('');
    setSending(true);
    try {
      const photos = [];
      const videos = [];
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append('file', f);
          fd.append('upload_preset', 'mjfans');
          const up = await fetch('https://api.cloudinary.com/v1_1/dk7pzawcd/auto/upload', { method: 'POST', body: fd });
          const r = await up.json();
          if (r.secure_url) { (r.resource_type === 'video' ? videos : photos).push(r.secure_url); }
        } catch (_) {}
      }
      const mjtoken = localStorage.getItem('mjtoken');
      const endpoint = isSubmission ? '/api/submissions' : '/api/testimonials';
      const payload = isSubmission ? { ...form, type: form.section, photos, videos } : { ...form, photos, videos };
      await axios.post(endpoint, payload, { headers: { 'Content-Type': 'application/json', ...(mjtoken ? { Authorization: 'Bearer ' + mjtoken } : {}) } });
      setSent(true);
    } catch (e) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    }
    setSending(false);
  };

  if (sent) return (
    <div style={{ minHeight: '100vh', background: '#fafaf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', maxWidth: '480px', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎤</div>
        <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '28px', color: '#1a1010', marginBottom: '12px' }}>{(form.section === 'actualite' || form.section === 'info') ? 'Merci pour votre envoi' : 'Merci pour votre témoignage'}</h2>
        <p style={{ fontSize: '14px', color: '#9a8080', lineHeight: '1.8', marginBottom: '28px' }}>{(form.section === 'actualite' || form.section === 'info') ? 'Votre envoi a bien été reçu. Merci de faire vivre la mémoire de Michael. ♥' : 'Votre témoignage a bien été reçu. Notre équipe le relira dans les prochains jours avant de le publier. Michael vous remercie. ♥'}</p>
        <button onClick={() => navigate('/')} style={{ background: '#8b1a2a', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 28px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Retour à l'accueil</button>
      </div>
    </div>
  );

  const inputStyle = { width: '100%', border: '1px solid #dccfc8', borderRadius: '7px', padding: '11px 13px', fontSize: '14px', color: '#1a1010', background: '#fff', fontFamily: 'Arial,sans-serif', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: '13px', color: '#6a5050', marginBottom: '6px', fontWeight: '500' };

  return (
    <div style={{ background: '#fafaf8', minHeight: '100vh' }}>
      <div style={{ background: '#fff', padding: '28px 40px', borderBottom: '1px solid #dccfc8' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <p style={{ fontSize: '12px', letterSpacing: '0.15em', color: '#8b1a2a', marginBottom: '8px' }}>— PARTAGER MON TÉMOIGNAGE</p>
        <h1 style={{ fontFamily: 'Georgia,serif', fontSize: '30px', color: '#1a1010', fontWeight: '400', marginBottom: '6px' }}>✦ Témoigner</h1>
        <p style={{ fontSize: '14px', color: '#9a8080', lineHeight: '1.6' }}>Votre témoignage sera relu par notre équipe avant publication. Merci de remplir tous les champs obligatoires.</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ padding: '36px 40px', borderRight: '1px solid #dccfc8' }}>

          {/* Étape 1 */}
          <div style={{ marginBottom: '32px' }}>
            <p style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#8b1a2a', marginBottom: '10px', fontWeight: '500' }}>— ÉTAPE 1</p>
            <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '18px', color: '#1a1010', marginBottom: '16px' }}>Dans quelle section souhaitez-vous témoigner ?</h2>
            <div className="temoigner-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {sections.map(s => (
                <div key={s.id} onClick={() => update('section', s.id)}
                  onMouseEnter={e => { if (form.section !== s.id) { e.currentTarget.style.borderColor = '#c49090'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(139,26,42,0.14)'; e.currentTarget.style.transform = 'translateY(-3px)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = form.section === s.id ? '#8b1a2a' : '#dccfc8'; e.currentTarget.style.boxShadow = form.section === s.id ? '0 2px 8px rgba(139,26,42,0.12)' : '0 1px 3px rgba(139,26,42,0.05)'; e.currentTarget.style.transform = 'none'; }}
                  style={{ position: 'relative', border: `1px solid ${form.section === s.id ? '#8b1a2a' : '#dccfc8'}`, borderRadius: '8px', padding: '16px', background: form.section === s.id ? '#f5eeeb' : '#fff', cursor: 'pointer', transition: 'all 0.15s', boxShadow: form.section === s.id ? '0 2px 8px rgba(139,26,42,0.12)' : '0 1px 3px rgba(139,26,42,0.05)' }}>
                  {form.section === s.id && <div style={{ position: 'absolute', top: '8px', right: '10px', width: '18px', height: '18px', borderRadius: '50%', background: '#8b1a2a', color: '#fff', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>}
                  <div style={{ fontSize: '22px', marginBottom: '6px' }}>{s.icon}</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1010', marginBottom: '4px' }}>{s.name}</div>
                  <div style={{ fontSize: '12px', color: '#9a8080' }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Étape 2 */}
          <div style={{ marginBottom: '32px' }}>
            <p style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#8b1a2a', marginBottom: '10px', fontWeight: '500' }}>— ÉTAPE 2</p>
            <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '18px', color: '#1a1010', marginBottom: '16px' }}>Vos informations</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Prénom ou pseudonyme *</label>
                <input style={{ ...inputStyle, ...(user && form.authorName ? { background: '#f5eeeb', color: '#6a5050', cursor: 'not-allowed' } : {}) }} placeholder="Marie-Laure" value={form.authorName} onChange={e => update('authorName', e.target.value)} readOnly={!!(user && form.authorName)} />
                {user && form.authorName && <div style={{ fontSize: '12px', color: '#9a8080', marginTop: '4px' }}>Connecté·e en tant que {form.authorName}</div>}
              </div>
              <div>
                <label style={labelStyle}>Pays *</label>
                <select style={inputStyle} value={form.authorCountry} onChange={e => update('authorCountry', e.target.value)}>
                  <option value="">Sélectionner...</option>
                  {pays.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Ville de l'événement</label>
                <input style={inputStyle} placeholder="Paris" value={form.city} onChange={e => update('city', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Année</label>
                <input style={inputStyle} placeholder="1997" value={form.year} onChange={e => update('year', e.target.value)} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Nom de l'événement / tournée</label>
              <input style={inputStyle} placeholder="HIStory World Tour — Bercy" value={form.eventName} onChange={e => update('eventName', e.target.value)} />
            </div>
          </div>

          {/* Étape 3 */}
          <div style={{ marginBottom: '32px' }}>
            <p style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#8b1a2a', marginBottom: '10px', fontWeight: '500' }}>— ÉTAPE 3</p>
            <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '18px', color: '#1a1010', marginBottom: '16px' }}>Votre témoignage *</h2>
            <textarea style={{ ...inputStyle, minHeight: '140px', resize: 'vertical', lineHeight: '1.7' }} placeholder="Racontez votre souvenir, votre ressenti, ce moment unique avec Michael…" value={form.content} onChange={e => update('content', e.target.value)} />
            <div style={{ fontSize: '12px', color: '#c0b0b0', marginTop: '6px', textAlign: 'right' }}>{form.content.length} caractères</div>
          </div>

          {/* Étape 4 */}
          <div style={{ marginBottom: '32px' }}>
            <p style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#8b1a2a', marginBottom: '10px', fontWeight: '500' }}>— ÉTAPE 4 (optionnel)</p>
            <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '18px', color: '#1a1010', marginBottom: '16px' }}>Photos ou vidéos</h2>
            <div
              onClick={() => fileRef.current.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              style={{ border: `1px dashed ${dragOver ? '#8b1a2a' : '#c49090'}`, borderRadius: '10px', padding: '28px', textAlign: 'center', background: dragOver ? '#f5eeeb' : '#fdf8f6', cursor: 'pointer', transition: 'all 0.15s' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📎</div>
              <div style={{ fontSize: '14px', color: '#9a8080', marginBottom: '4px' }}>Glissez vos fichiers ici ou cliquez pour parcourir</div>
              <div style={{ fontSize: '12px', color: '#c0b0b0', marginBottom: '6px' }}>JPG, PNG, MP4, MOV, AVI, MKV acceptés</div>
              <div style={{ fontSize: '12px', color: '#8b1a2a', fontWeight: '500' }}>⬆ Jusqu'à 5 Go par fichier — qualité originale bienvenue</div>
            </div>
            <input ref={fileRef} type="file" multiple accept="image/*,video/*" style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0 12px' }}>
              <div style={{ flex: 1, height: 1, background: '#e8d0d0' }} />
              <div style={{ fontSize: 12, color: '#9a8080' }}>ou, pour un film entier</div>
              <div style={{ flex: 1, height: 1, background: '#e8d0d0' }} />
            </div>
            <label style={labelStyle}>Lien video (YouTube, Vimeo)</label>
            <input style={inputStyle} placeholder="https://youtube.com/watch?v=..." value={form.videoUrl} onChange={e => update('videoUrl', e.target.value)} />
            {files.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#fff', border: '1px solid #dccfc8', borderRadius: '6px', marginBottom: '6px' }}>
                    <div>
                      <div style={{ fontSize: '13px', color: '#1a1010' }}>{f.name}</div>
                      <div style={{ fontSize: '12px', color: '#9a8080' }}>{(f.size / 1024 / 1024).toFixed(1)} Mo</div>
                    </div>
                    <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', color: '#c49090', cursor: 'pointer', fontSize: '16px' }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Étape 5 */}
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#8b1a2a', marginBottom: '10px', fontWeight: '500' }}>— ÉTAPE 5</p>
            <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '18px', color: '#1a1010', marginBottom: '16px' }}>Droits et consentement</h2>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px', background: '#fff', border: `1px solid ${form.rightsAccepted ? '#8b1a2a' : '#dccfc8'}`, borderRadius: '8px', cursor: 'pointer' }} onClick={() => update('rightsAccepted', !form.rightsAccepted)}>
              <input type="checkbox" checked={form.rightsAccepted} onChange={() => {}} style={{ marginTop: '3px', flexShrink: 0, accentColor: '#8b1a2a', width: '16px', height: '16px' }} />
              <div style={{ fontSize: '13px', color: '#4a3030', lineHeight: '1.7' }}>
                En soumettant ce témoignage, j'accepte de céder à titre gratuit les droits de reproduction et d'utilisation de mon contenu (texte, photos, vidéos, œuvres) dans le cadre de ce site, de ses évolutions futures, d'un éventuel documentaire audiovisuel, ainsi que de toute publication imprimée ou format papier (livre, magazine, recueil…), tout en conservant la <strong style={{ color: '#8b1a2a' }}>mention de mon nom si je le souhaite.</strong> *
              </div>
            </div>
          </div>

          {error && <div style={{ background: '#fff0f0', border: '1px solid #e08080', borderRadius: '7px', padding: '12px 16px', fontSize: '13px', color: '#a03030', marginBottom: '14px' }}>{error}</div>}

          <button onClick={handleSubmit} disabled={sending} style={{ background: sending ? '#c49090' : '#8b1a2a', color: '#fff', border: 'none', borderRadius: '8px', padding: '15px', fontSize: '15px', fontWeight: '500', cursor: sending ? 'not-allowed' : 'pointer', width: '100%' }}>
            {sending ? 'Envoi en cours…' : '✦ Envoyer mon témoignage'}
          </button>
        </div>

        {/* Colonne droite */}
        <div style={{ padding: '36px 28px', background: '#fdf8f6' }}>
          <h3 style={{ fontFamily: 'Georgia,serif', fontSize: '18px', color: '#1a1010', marginBottom: '20px' }}>Conseils pour un beau témoignage</h3>
          {[
            { icon: '💬', title: 'Soyez précis', text: 'Une date, un lieu, une émotion précise — les détails rendent votre témoignage vivant et unique.' },
            { icon: '📸', title: 'Vos photos sont précieuses', text: 'Même floues, même vieilles — elles témoignent d\'un moment réel. Envoyez-les en qualité originale, nous nous occupons du reste.' },
            { icon: '🌍', title: 'Toutes les langues acceptées', text: 'Témoignez dans votre langue. Nous acceptons le français, l\'anglais, l\'espagnol, le portugais et bien d\'autres.' },
            { icon: '✨', title: 'Pas besoin d\'avoir été à un concert', text: 'Un souvenir d\'écoute, une émotion, un rêve — tout témoignage sur Michael est le bienvenu.' },
          ].map((tip, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid #dccfc8', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
              <div style={{ fontSize: '22px', marginBottom: '7px' }}>{tip.icon}</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#1a1010', marginBottom: '5px' }}>{tip.title}</div>
              <div style={{ fontSize: '13px', color: '#9a8080', lineHeight: '1.6' }}>{tip.text}</div>
            </div>
          ))}
          <div style={{ background: '#f5eeeb', border: '1px solid #c49090', borderRadius: '8px', padding: '16px', marginTop: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#8b1a2a', marginBottom: '5px' }}>🔍 Modération avant publication</div>
            <div style={{ fontSize: '13px', color: '#6a5050', lineHeight: '1.6' }}>Chaque témoignage est relu avant d'être publié. Aucun message haineux accepté. Délai habituel : 3 à 7 jours.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
